import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Search, Undo2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAdminSession } from '@/lib/adminAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function relativeFromNow(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function daysRemaining(deletedAt, retention) {
  if (!deletedAt) return retention;
  const d = new Date(deletedAt).getTime();
  const elapsed = Math.floor((Date.now() - d) / 86400000);
  return Math.max(0, retention - elapsed);
}

export default function AdminDeletedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = location.state?.currentUser || getAdminSession();

  const [items, setItems] = useState([]);
  const [retention, setRetention] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'superadmin') {
      toast.error('Access denied. Super Admin only.');
      navigate('/admin/waitlist');
      return;
    }
    fetchDeleted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDeleted = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/deleted`);
      setItems(data.items || []);
      setRetention(data.retention_days || 30);
    } catch (err) {
      toast.error('Failed to load deleted entries');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async (item) => {
    const key = `${item.type}:${item.email}`;
    setBusyKey(key);
    try {
      await axios.post(`${API}/admin/restore`, { type: item.type, email: item.email });
      // audit
      await axios.post(`${API}/admin/audit`, {
        admin_email: currentUser.email,
        admin_name: currentUser.name,
        action: 'restore',
        target: item.type,
        entry_id: item.email,
        changes: `Restored ${item.type} entry for ${item.email}`,
      }).catch(() => {});
      toast.success(`Restored ${item.email}`);
      setItems(prev => prev.filter(i => !(i.type === item.type && i.email === item.email)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore');
    } finally {
      setBusyKey(null);
    }
  };

  const handlePurge = async (item) => {
    if (!window.confirm(`Permanently delete ${item.email}? This cannot be undone.`)) return;
    const key = `${item.type}:${item.email}`;
    setBusyKey(key);
    try {
      await axios.delete(`${API}/admin/deleted/${item.type}/${encodeURIComponent(item.email)}`);
      await axios.post(`${API}/admin/audit`, {
        admin_email: currentUser.email,
        admin_name: currentUser.name,
        action: 'purge',
        target: item.type,
        entry_id: item.email,
        changes: `Permanently purged ${item.type} entry for ${item.email}`,
      }).catch(() => {});
      toast.success(`Permanently removed ${item.email}`);
      setItems(prev => prev.filter(i => !(i.type === item.type && i.email === item.email)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to purge');
    } finally {
      setBusyKey(null);
    }
  };

  const filtered = items.filter(i => {
    const q = searchTerm.toLowerCase();
    if (!q) return true;
    return (
      (i.email || '').toLowerCase().includes(q) ||
      (i.name || '').toLowerCase().includes(q) ||
      (i.club_name || '').toLowerCase().includes(q) ||
      (i.contact_name || '').toLowerCase().includes(q)
    );
  });

  if (!currentUser) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground font-['Space_Grotesk']" data-testid="recently-deleted-title">
                Recently Deleted
              </h1>
              <Badge className="bg-destructive/15 text-destructive border-destructive/30">
                Super Admin
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Entries are kept for <span className="font-medium text-foreground">{retention} days</span> after deletion, then permanently removed.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={fetchDeleted} disabled={isLoading} data-testid="refresh-deleted-btn">
              Refresh
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/waitlist')} data-testid="back-to-admin-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card className="bg-card border-border/50 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search deleted entries by email, name or club..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-muted/50 border-border/50"
              data-testid="search-deleted-input"
            />
          </div>
        </Card>

        {/* Table */}
        <Card className="bg-card border-border/50 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="text-foreground">Type</TableHead>
                  <TableHead className="text-foreground">Email</TableHead>
                  <TableHead className="text-foreground">Name / Club</TableHead>
                  <TableHead className="text-foreground">Role</TableHead>
                  <TableHead className="text-foreground">Country</TableHead>
                  <TableHead className="text-foreground">Deleted</TableHead>
                  <TableHead className="text-foreground">Auto-purge in</TableHead>
                  <TableHead className="text-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-muted-foreground">
                      Loading deleted entries...
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="no-deleted-entries">
                      Nothing in the bin. Deleted entries will appear here.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((item) => {
                    const key = `${item.type}:${item.email}`;
                    const remaining = daysRemaining(item.deleted_at, retention);
                    const nameOrClub = item.type === 'pilot'
                      ? (item.club_name || item.contact_name || '-')
                      : (item.name || '-');
                    return (
                      <TableRow key={key} className="border-border/50" data-testid={`deleted-row-${item.type}-${item.email}`}>
                        <TableCell>
                          <Badge className={item.type === 'pilot'
                            ? 'bg-accent/15 text-accent border-accent/30 capitalize'
                            : 'bg-primary/15 text-primary border-primary/30 capitalize'}>
                            {item.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-foreground">{item.email}</TableCell>
                        <TableCell className="text-foreground">{nameOrClub}</TableCell>
                        <TableCell className="text-foreground capitalize">{item.role || '-'}</TableCell>
                        <TableCell className="text-foreground">{item.country || '-'}</TableCell>
                        <TableCell className="text-muted-foreground" title={item.deleted_at}>
                          {relativeFromNow(item.deleted_at)}
                        </TableCell>
                        <TableCell>
                          <span className={remaining <= 3 ? 'text-destructive font-medium' : 'text-foreground'}>
                            {remaining} day{remaining === 1 ? '' : 's'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyKey === key}
                              onClick={() => handleRestore(item)}
                              className="border-primary/40 text-primary hover:bg-primary/10"
                              data-testid={`restore-btn-${item.type}-${item.email}`}
                            >
                              <Undo2 className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={busyKey === key}
                              onClick={() => handlePurge(item)}
                              className="text-destructive hover:bg-destructive/10"
                              data-testid={`purge-btn-${item.type}-${item.email}`}
                              title="Permanently delete now"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}
